import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';

// Components
import RoundSelect from './RoundSelect';
import LangSelector from './LangSelector';
import About from './About';
import logo from '../assets/icon.png';

/* FontAwesome */
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimesCircle,
  faInfo,
  faRoute,
  faBars,
  faListAlt,
  faPowerOff,
  faUserFriends,
  faTachometerAlt,
  faTrashAlt,
  faPlusCircle,
  faRedoAlt,
  faFileAlt,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';

// Add FontAwesome icons
library.add([
  faTimesCircle,
  faInfo,
  faRoute,
  faBars,
  faListAlt,
  faPowerOff,
  faUserFriends,
  faTachometerAlt,
  faTrashAlt,
  faPlusCircle,
  faRedoAlt,
  faFileAlt,
  faQuestionCircle,
]);

const Header = ({ subtitle = true }) => {
  // State
  const [open, setOpen] = useState(false);
  const [about, setAbout] = useState(false);

  // Hooks
  const user = useSelector((state) => state.authentication.user);
  const game = useSelector((state) => state.game);
  const { t } = useTranslation();

  // Methods
  const toggleMenu = () => {
    setOpen(!open);
  };

  const toggleAbout = () => {
    setAbout(!about);
    setOpen(false);
  };

  const title = game.data ? game.data.name : '';
  const fromHBP = user && user.from && user.from === 'lti-hbp' ? true : false;
  const isInstructor =
    user && user.role && user.role === 'instructor' ? true : false;

  // Logo content
  const logoImg = (
    <img
      src={logo}
      className='logo'
      style={{
        height: '35px',
        marginRight: '15px',
      }}
      alt='logo'
    />
  );

  const logoSubtitle = subtitle && game && (
    <span
      className='d-inline-block font-weight-light text-primary ms-3'
      style={{
        maxWidth: '25vw',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
      {title}
    </span>
  );

  const { REACT_APP_LIBRARY_URL = null, REACT_APP_SIM_NAME = null } =
    process.env;

  const scProductUrl = REACT_APP_LIBRARY_URL + '?new=true';
  let materialsLink = REACT_APP_LIBRARY_URL + '?materials=true';
  let helpLink = 'https://simcase.help';

  // If user comes from HBPE
  if (fromHBP) {
    materialsLink = process.env.REACT_APP_HBP_PRODUCT_URL || '#';
    helpLink = 'https://help.hbsp.harvard.edu/hc/en-us';
  }

  return (
    <header
      className='app-header'
      style={{ margin: '0 calc(var(--bs-gutter-x) * -.5)' }}>
      <nav id='main-menu' className='navbar'>
        <div className='flex-md-shrink-1'>
          {user?.role === 'instructor' && fromHBP && (
            <span className='navbar-brand d-flex align-items-center title fs-4'>
              {logoImg}
              <span className='d-none d-md-inline'>{REACT_APP_SIM_NAME}</span>
              {logoSubtitle}
            </span>
          )}

          {(user?.role !== 'instructor' || !fromHBP) && (
            <Link
              className='navbar-brand d-flex align-items-center title fs-4'
              to='/games'>
              {logoImg}
              <span className='d-none d-md-inline'>
                {REACT_APP_SIM_NAME}
              </span>{' '}
              {logoSubtitle}
            </Link>
          )}
        </div>

        {game?.data?.sample !== true &&
          subtitle &&
          game?.data?.status === 'launched' && (
            <div
              className='d-md-inline-block order-3 order-md-2'
              style={{ padding: '15px 15px' }}>
              <RoundSelect />
            </div>
          )}

        {/* Buttons */}
        <div className='d-flex align-items-center order-3 order-lg-2 ms-md-auto'>
          {/* Note Links */}
          {REACT_APP_LIBRARY_URL !== null ? (
            <a
              href={materialsLink}
              className='btn btn-link text-muted ms-auto'
              target='_blank'
              rel='noreferrer'>
              <FontAwesomeIcon className='me-1' icon='file-alt' />{' '}
              <small>{t('Resources')}</small>
            </a>
          ) : null}

          {/* Help */}
          <a
            href={helpLink}
            className='btn btn-link text-muted ms-md-4'
            target='_blank'
            rel='noreferrer'>
            <FontAwesomeIcon className='me-1' icon='question-circle' />{' '}
            <small>{t('Help')}</small>
          </a>

          {/* Launched: Submenu buttons */}
          {game.data &&
            game.data.status === 'launched' && [
              <NavLink
                key='gd-setup'
                to={`/games/${game.data._id}/setup`}
                className={({ isActive }) =>
                  isActive
                    ? 'btn btn-primary text-white ms-4'
                    : 'btn btn-outline-primary ms-4'
                }>
                {t('Setup')}
              </NavLink>,

              <NavLink
                key='gd-players'
                to={`/games/${game.data._id}/players`}
                className={({ isActive }) =>
                  isActive
                    ? 'btn btn-primary text-white ms-4'
                    : 'btn btn-outline-primary ms-4'
                }>
                {t('Players')}
              </NavLink>,

              <NavLink
                key='gd-debrief'
                // to={`/games/${game.data._id}/debrief/overview`}
                to={`/games/${game.data._id}/debrief/dancefloor`}
                className={({ isActive }) =>
                  isActive
                    ? 'btn btn-primary text-white ms-4'
                    : 'btn btn-outline-primary ms-4'
                }>
                {t('Debrief')}
              </NavLink>,
            ]}

          {/* New Game button */}
          {!game.data && !fromHBP && (
            <a
              href={isInstructor ? scProductUrl : '/admin/games/new'}
              className='btn btn-outline-primary ms-5'>
              {t('Create Game')}
            </a>
          )}
        </div>

        {/* Menu Toggler */}
        <div
          className='menu-toggler float-end text-primary me-3 ms-4 order-2 order-lg-3'
          onClick={toggleMenu}>
          <FontAwesomeIcon
            icon='bars'
            size='2x'
            style={{ cursor: 'pointer' }}
          />
        </div>
      </nav>

      <div>
        {open && (
          <div key='menu'>
            <div className='side-menu-wrap'>
              <div className='side-menu'>
                <div className='menu-close' onClick={toggleMenu}>
                  <FontAwesomeIcon icon='times-circle' />
                </div>

                <div className='menu-item' onClick={toggleAbout}>
                  <span>
                    <FontAwesomeIcon icon='route' size='3x' className='icon' />
                    <h5 className='mb-0'>{t('About')}</h5>
                    <small className='mt-0 mb-0'>
                      {t('About this SimCase')}
                    </small>
                  </span>
                </div>

                {(user?.role !== 'instructor' || !fromHBP) && (
                  <div className='menu-item mt-3' onClick={toggleMenu}>
                    <NavLink to='/games'>
                      <FontAwesomeIcon
                        icon='list-alt'
                        size='3x'
                        className='icon'
                      />
                      <h5 className='mb-0'>{t('Game Sessions')}</h5>
                      <small className='mt-0 mb-0'>{t('List of games')}</small>
                    </NavLink>
                  </div>
                )}

                {!fromHBP && (
                  <div className='menu-item mt-3' onClick={toggleMenu}>
                    <NavLink to='/logout'>
                      <FontAwesomeIcon
                        icon='power-off'
                        size='3x'
                        className='icon'
                      />
                      <h5 className='mb-0'>{t('Log Out')}</h5>
                      <small className='mt-0 mb-0'>
                        {t('Close your session')}
                      </small>
                    </NavLink>
                  </div>
                )}

                <LangSelector />
              </div>
              <div onClick={toggleMenu} className='side-menu-overlay'></div>
            </div>
          </div>
        )}
      </div>

      {/* About Modal */}
      <Modal isOpen={about} toggle={toggleAbout} centered={true}>
        <ModalBody className='text-center'>
          <About onClose={toggleAbout} />
        </ModalBody>
      </Modal>
    </header>
  );
};

export default Header;

{
  /* About Modal */
}
{
  /* <Modal
isOpen={this.state.about}
toggle={this.toggleAbout}
centered={true}>
<ModalBody className='text-center'>
  <div className='mt-4 mb-4'>
    <div style={{ padding: '0 10%' }}>
      <h4 className='mt-0'>About Zephyr</h4>
      <p className='mt-5'>
        Written by
        <br />
        W. Trexler Proffitt, Jr. and Gaylen D. Paulson
      </p>
      <img src={logoKellogg} className='img-fluid' alt='Kellogg' />
      <p className='mt-5'>Produced by SimCase</p>
      <img
        src={logoWide}
        className='img-fluid'
        style={{ maxHeight: '50px' }}
        alt='SimCase'
      />
      <p className='mt-5 mb-0'>
        <span
          className='btn btn-outline-primary'
          onClick={this.toggleAbout}>
          Close
        </span>
      </p>
    </div>
  </div>
</ModalBody>
</Modal> */
}
